import axios from 'axios';
import moment from 'moment-timezone';
import { CronExpressionParser } from 'cron-parser';
import { T_User } from '@repo-sapiens/data-access-models';
export function getNextRunTimeInUTC(
  cronExpression: string,
  userTimezone: string
) {
  const currentTime = moment.tz(userTimezone).toDate();

  const interval = CronExpressionParser.parse(cronExpression, {
    currentDate: currentTime,
    tz: userTimezone,
  });

  const nextRunTime = interval.next().toDate();

  // const nextRunTimeInUTC = moment(nextRunTime).utc().format();

  // const nextRunTimeInUserTimezone = moment(nextRunTime)
  //   .tz(userTimezone)
  //   .format();

  return nextRunTime;

  // return new Date(nextRunTimeInUTC);
}

export function formatDate(date: Date, timeZone: string, format?: string) {
  const nowUtc = moment.utc(date);
  const localTime = nowUtc.tz(timeZone);
  return localTime.format(format || 'dddd, MMMM D, yyyy • h:mm');
}
export function isValidUrl(text: string) {
  try {
    new URL(text);
    return true;
  } catch {
    return false;
  }
}

export function extractTimeFromDate(date: string | Date, timeZone?: string) {
  const time = moment(date)
    .tz(timeZone || 'America/New_York')
    .format('HH:mm');
  return formatTimeToPM(time);
}

export function formatTimeToPM(timeString: string) {
  // Remove any timezone part if present
  // const [time] = timeString?.split('-') || [];

  // if (!time) {
  //   return '';
  // }
  //
  if (!timeString) {
    return '';
  }

  // Split the time into hours, minutes, and seconds
  const [hours, minutes] = timeString.split(':').map(Number);

  // Determine AM or PM
  const period = hours >= 12 ? 'PM' : 'AM';

  // Convert hours from 24-hour to 12-hour format
  const formattedHours = hours % 12 || 12; // '0' should be '12'

  // Return the formatted time string
  return `${formattedHours}:${
    minutes?.toString()?.padStart(2, '0') || ''
  } ${period}`;
}

export function extractJSONObject(str: string) {
  // Regular expression to match a JSON object in a string
  const jsonRegex = /({.*})/;

  const jsonStringMatch = str.match(jsonRegex);

  if (!jsonStringMatch || jsonStringMatch.length < 2) {
    return;
  }

  let jsonString = jsonStringMatch[1];

  // Fix common JSON issues
  jsonString = jsonString
    .replace(/(\w+):/g, '"$1":') // Add quotes around keys
    .replace(/:\s*(\w+)/g, ': "$1"') // Add quotes around values
    .replace(/\\\\"/g, '"'); // Replace escaped quotes

  try {
    const jsonObject = JSON.parse(jsonString);
    return jsonObject;
  } catch {
    return;
  }
}

export const getEmailDomain = (email: string) => email.split('@')[1];

export function isPersonalEmail(email: string) {
  const isPersonalEmailRegex =
    /@(gmail|yahoo|hotmail|yahoo|aol|abc|xyz|pqr|rediffmail|live|outlook|me|msn|ymail)\.com$/;
  return isPersonalEmailRegex.test(email);
}

export function getRandomItem<T>(items: T[]): T {
  if (items.length === 0) {
    throw new Error('Array is empty');
  }
  const randomIndex = Math.floor(Math.random() * items.length);
  return items[randomIndex];
}

export async function validateImageUrl(url: string): Promise<string | null> {
  try {
    const response = await axios.head(url);
    if (response.status >= 200 && response.status < 400) {
      return url;
    } else {
      return null;
    }
  } catch {
    return null;
  }
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function removeDuplicates<T extends Record<string, unknown>>(
  array: T[],
  uniqueKey: string
) {
  return array.filter(
    (v, i, a) => a.findIndex((t) => t[uniqueKey] === v[uniqueKey]) === i
  );
}

export function splitArrayIntoChunks<T>(arr: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    return [];
  }

  if (!arr?.length) {
    return [];
  }

  const batches = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    batches.push(arr.slice(i, i + chunkSize));
  }

  return batches;
}

export function getMeetingLocation(name: string) {
  if (name?.includes('zoom')) {
    return 'Zoom';
  } else if (name?.includes('meet.google')) {
    return 'Google Meet';
  } else if (
    name?.toLowerCase().includes('room') ||
    name?.toLowerCase().includes('office')
  ) {
    return 'In-Person';
  } else if (name?.toLowerCase().includes('virtual')) {
    return 'Virtual';
  }
  return 'In-Person';
}

/**
 * Return the object with the most non null fields from the provided fields
 * @param object1 - The first object
 * @param object2 - The second object
 * @param fields - The fields to compare
 * @returns The object with the most non null fields
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getMostUpdatedObject<T extends Record<string, any>>(
  object1: T,
  object2: T,
  fields: (keyof T)[]
) {
  let object1Score = 0;
  let object2Score = 0;

  for (const field of fields) {
    if (object1[field]) {
      object1Score++;
    }
    if (object2[field]) {
      object2Score++;
    }

    if (object1[field] && object2[field]) {
      const length1 = object1[field].length;
      const length2 = object2[field].length;
      if (length1 > length2) {
        object1Score++;
      } else if (length2 > length1) {
        object2Score++;
      }
    }
  }

  return object1Score >= object2Score ? object1 : object2;
}

export function parseEmailSender(sender: string): {
  name?: string;
  email: string;
} {
  if (!sender) {
    return { name: '', email: '' };
  }
  const regex = /^(.*?)\s*<([^>]+)>$/;
  const matches = sender?.match(regex);

  if (matches) {
    return {
      name: matches[1].trim(),
      email: matches[2].trim(),
    };
  }

  // if the entire string is an email
  return {
    name: '',
    email: sender?.trim(),
  };
}
export function parseEmailRecipients(
  recipients: string
): { name: string; email: string }[] {
  const regex = /([^<]+)<([^>]+)>/g;
  const matches = recipients?.matchAll(regex);

  if (!matches) {
    return [];
  }

  const parsedRecipients = [];
  for (const match of matches) {
    parsedRecipients.push({
      name: match[1].trim(),
      email: match[2].trim(),
    });
  }

  return parsedRecipients;
}

export async function fetchImageAsBase64(url: string): Promise<string> {
  try {
    const response = await axios.get(url, { responseType: 'arraybuffer' });

    const base64 = Buffer.from(response.data, 'binary').toString('base64');

    return `${base64}`;
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error;
  }
}

export class HashingUtil {
  private static generateRandomBytes(length: number): string {
    // Use browser's crypto.getRandomValues instead of Node's randomBytes
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }

  private static async browserScrypt(
    password: string,
    salt: string,
    keyLength: number
  ): Promise<ArrayBuffer> {
    // Use SubtleCrypto API to create a key
    const encoder = new TextEncoder();
    const passwordData = encoder.encode(password + salt);

    // Create a key from the password
    const key = await window.crypto.subtle.importKey(
      'raw',
      passwordData,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );

    // Derive bits using PBKDF2 (closest browser equivalent to scrypt)
    const derivedBits = await window.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: 1000,
        hash: 'SHA-256',
      },
      key,
      keyLength * 8
    );

    return derivedBits;
  }

  public static async hash(value: string, salt = '') {
    const _salt = salt || this.generateRandomBytes(8);
    const hashBuffer = await this.browserScrypt(value, _salt, 32);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return `${hashHex}.${_salt}`;
  }

  public static async compare(value: string, hashedSaltedValue: string) {
    const [, salt] = hashedSaltedValue.split('.');
    const hashedPassword = await HashingUtil.hash(value, salt);
    return hashedSaltedValue === hashedPassword;
  }
}

/**
 * Color from Id
 */
export function strToNum(str: string) {
  let num = 0;
  for (let i = 0; i < str?.length || 0; i++) {
    num = str.charCodeAt(i) + ((num << 5) - num);
  }
  return num;
}

export function color(str: string) {
  const num = strToNum(str);
  return `hsl(${~~(num % 360)},70%,70%)`;
}

type Params = Record<string, string>;

export function extractFromQuery<P extends object = Params>(query: string) {
  const result = {} as P;
  const str = query.startsWith('?') ? query.slice(1) : query;
  const parts = str.split('&');
  const pairs = parts.map((part) => part.split('='));

  for (const [key, value] of pairs) {
    const k = decodeURIComponent(key) as keyof P;
    const v = decodeURIComponent(value) as unknown as P[keyof P];
    result[k] = v;
  }

  return result;
}

export function displayUser(user: T_User) {
  return user?.firstName
    ? `${user.firstName} ${user?.lastName || ''}`
    : user && user?.email
    ? user?.email
    : null;
}

export function secondsToMinutes(sec: number) {
  // Calculate minutes and remaining seconds
  const minutes = Math.floor(sec / 60);
  const seconds = (sec % 60).toFixed(0);

  // Convert minutes and seconds to strings and add leading zeros if needed
  const minutesStr = String(minutes).padStart(2, '0');
  const secondsStr = String(seconds).padStart(2, '0');

  // Return the time in mm:ss format
  return `${minutesStr}:${secondsStr}`;
}

export function formatTimeToAM(date: Date) {
  const theDate = new Date(date);
  let hours = theDate.getHours();
  const minutes = theDate.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  // // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minString = minutes < 10 ? '0' + minutes : minutes.toString();

  const strTime = hours + ':' + minString + ' ' + ampm;
  return strTime;
}

export const extractMeetingLocation = (location: string) => {
  if (location?.includes('http')) {
    return location?.split('/')[2]?.split('.')?.slice(-2)?.join('.') || '';
  }
  return location;
};

export const isBetaUser = (user: string) => {
  const betaUsers = [
    '6682b798ba35da9fbdd685a6', // djibril.issoufou@multiplayer.work
    '66f43bf41b53993eca10abb2', // craig.hosang@multiplayer.work
    '668c76d873eff9d43033f912',
    '66ba399d353764d12439bd03',
    '66d8c42a44a130c00d62418e',
    '6682d86df4401cc0f20882c8',
    '67ac6b90dfdadb12eb29b83a',
    '67db59cccf863f6c760d69a7',
    '66ea02d2fe698d7fb13a98da',
    '67e19a32a16c56a108052ea9',
    '67a26beb859c1a547c84899a', // elizabeth keen
    '668d83bee82336149db3c71c', //  Alex
  ];
  return betaUsers.includes(user?.toString());
};

export function tryWithExponentialBackoff<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> {
  return new Promise((resolve, reject) => {
    const attempt = async (n: number) => {
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        console.error(`Attempt ${n + 1} failed:`);
        if (n <= retries) {
          setTimeout(() => attempt(n + 1), delay * Math.pow(2, n));
        } else {
          reject(error);
        }
      }
    };
    attempt(0);
  });
}
